import { useEffect, useState } from "react";
import { Table } from "@mantine/core";
import moment from "moment";
import QRCode from "react-qr-code";
import {
    Mail,
    Phone,
    ArrowRight,
} from "lucide-react";

import { RefundFeeSummary } from "libs/ui";
import { humanizeText, numberToWords } from "utils/utility";
import { useUsersData } from "libs/data-access/src/api/usersData/useUsersData";
import { IFlightOnlineBookingList, IFlightOnlineBookingPaymentOrder, IUserDetailsForInvoice } from "libs/data-access/types";

import { manageFlightCharges } from "../../hooks/utility";

interface IProps {
    flightTicket: IFlightOnlineBookingList;
};

export default function FlightRefundAdvice({ flightTicket }: IProps) {

    const { getUsersData } = useUsersData();

    const [userDetails, setUserDetails] = useState<IUserDetailsForInvoice>({
        id: 0,
        name: "",
        emailId: "",
        phoneNumber: "",
        affiliateDetailForInvoice: {
            country: null,
            city: null,
            state: null,
            pinCode: null,
            firstAddress: null,
            secondAddress: null,
            companyName: null,
        }
    });
    const [fareDetails, setFareDetails] = useState<{
        paymentDetails: IFlightOnlineBookingPaymentOrder | undefined;
        supplierId: number;
        fareBreakup: {
            airlineBaseFare: number;
            airlineTax: number;
            serviceCharges: number;
            otherCharges: number;
            totalGst: number;
            totalFare: number;
        };
    }>({
        paymentDetails: undefined,
        supplierId: 0,
        fareBreakup: {
            airlineBaseFare: 0,
            airlineTax: 0,
            serviceCharges: 0,
            otherCharges: 0,
            totalGst: 0,
            totalFare: 0,
        }
    });


    useEffect(() => {
        getUserDetails();
        manageFareBreakup();
    }, [flightTicket]);


    // HANDLER FUNCTIONS
    const manageFareBreakup = () => {

        const paymentDetails = flightTicket?.paymentOrder;
        const supplierId = flightTicket?.bookingResponses?.[0]?.flightSupplierId;

        let fareBreakup = {
            airlineBaseFare: 0,
            airlineTax: 0,
            serviceCharges: 0,
            otherCharges: 0,
            totalGst: 0,
            totalFare: 0,
        };

        // airline base fare
        fareBreakup.airlineBaseFare =
            supplierId === 3
                ? paymentDetails?.galelioFlightOrderDetail?.flightBasePrice || 0
                : paymentDetails?.naviatorOrderDetail?.indigoBasePrice || 0;

        // airline tax
        fareBreakup.airlineTax =
            supplierId === 3
                ? paymentDetails?.galelioFlightOrderDetail?.flightTaxPrice || 0
                : paymentDetails?.naviatorOrderDetail?.indigoTaxPrice || 0;

        // service charges
        fareBreakup.serviceCharges =
            (paymentDetails?.adminBaseCharge || 0) +
            (paymentDetails?.adminServiceCharge || 0) +
            (paymentDetails?.adminBookingCharge || 0) +
            (paymentDetails?.affiliateBaseCharge || 0) +
            (paymentDetails?.affiliateServiceCharge || 0) +
            (paymentDetails?.affiliateBookingCharge || 0);

        // other charges
        fareBreakup.otherCharges = (paymentDetails?.adminOtherCharge || 0);

        // total gst
        fareBreakup.totalGst = (paymentDetails?.adminTotalGst || 0);

        // total fare
        fareBreakup.totalFare = (
            supplierId === 3
                ? paymentDetails?.galelioFlightOrderDetail?.flightBasePrice || 0
                : paymentDetails?.naviatorOrderDetail?.indigoBasePrice || 0) +
            (supplierId === 3
                ? paymentDetails?.galelioFlightOrderDetail?.flightTaxPrice || 0
                : paymentDetails?.naviatorOrderDetail?.indigoTaxPrice || 0) +
            (paymentDetails?.adminBaseCharge || 0) +
            (paymentDetails?.adminServiceCharge || 0) +
            (paymentDetails?.adminBookingCharge || 0) +
            (paymentDetails?.adminOtherCharge || 0) +
            (paymentDetails?.adminTotalGst || 0) +
            (paymentDetails?.affiliateBaseCharge || 0) +
            (paymentDetails?.affiliateServiceCharge || 0) +
            (paymentDetails?.affiliateBookingCharge || 0);
        // (paymentDetails?.affiliateOtherCharges || 0) +
        // (paymentDetails?.affiliateTotalGst || 0);

        setFareDetails({
            paymentDetails,
            supplierId,
            fareBreakup
        });

    };


    // API CALLS
    const getUserDetails = async () => {

        if (!flightTicket?.bookingResponses?.[0]?.id) return;

        const response = await getUsersData(`/details/invoice?bookingId=${flightTicket?.bookingResponses?.[0]?.id}&category=${"FLIGHT"}`);

        if (response.isSuccess) {
            // console.log("Response for user details: ", response.data);
            setUserDetails(response.data?.data);
        } else {
            console.log("Error while fetching user details: ", response.data);
        };
    };


    return (
        <div className="p-6 max-w-5xl mx-auto bg-white rounded-lg print:shadow-none">
            {/* HEADER */}
            <div className="flex justify-between items-start pb-6 border-b border-gray-300">
                <div className="text-gray-800">
                    <img
                        className="h-20 w-auto mb-6"
                        src={require("../../../../../assets/logos/travelyatra.jpeg")}
                        alt="Paul Merchants Logo"
                    />
                    <p className="text-base font-semibold">Paul Merchants Ltd.</p>
                    <div className="text-sm flex items-center gap-1">
                        SCO 829-830, Sector 22A Chandigarh, Chandigarh
                    </div>
                    <div className="text-sm">FE.CG/AD-II/01/2008 NDL-ADII-0001-2023</div>
                    <div className="text-sm mt-2">
                        <span className="font-semibold">GSTIN No: </span>
                        <span>04AAACP5609D1ZW</span>,
                        <span className="font-semibold ml-1"> CIN No: </span>
                        <span>L74900DL1984PLC018679</span>
                    </div>
                    <div className="text-sm flex gap-4 items-center">
                        <div className="flex items-center gap-1">
                            <Phone className="size-3.5" />
                            <span>0172 - 5041735, 01725041735</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Mail className="size-3.5" />
                            <span>del.travelaccounts@paulmerchants.net</span>
                        </div>
                    </div>
                </div>
                <div className="text-right flex flex-col justify-end items-end text-gray-700 text-sm">
                    <div className="text-4xl font-bold tracking-tight text-gray-800 mb-2">
                        Refund Advice
                    </div>
                    {flightTicket?.bookingResponses?.[0]?.invoiceNo && (
                        <div>
                            No. {flightTicket?.refundNo || ""}
                        </div>
                    )}
                    <div>
                        Issue on{" "}
                        {flightTicket?.bookingResponses?.[0]?.bookingDate
                            ?
                            moment(flightTicket?.bookingResponses?.[0]?.bookingDate).format("DD/MM/YYYY")
                            :
                            ""}
                    </div>
                </div>
            </div>

            {/* CUSTOEMR DETAILS */}
            <div className="my-12 ">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-600 mb-1 font-medium">To,</p>
                        <p className="text-lg font-semibold text-gray-800">
                            {userDetails.name}
                        </p>
                        {
                            userDetails.affiliateDetailForInvoice.companyName
                                ?
                                <p className="font-semibold text-gray-800">
                                    {userDetails.affiliateDetailForInvoice.companyName}
                                </p>
                                :
                                <></>
                        }
                        <p className="text-sm text-gray-600 mt-1">
                            {userDetails.emailId}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                            {userDetails.phoneNumber}
                        </p>
                        {
                            userDetails.affiliateDetailForInvoice.firstAddress
                                ?
                                <>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {`${userDetails.affiliateDetailForInvoice.firstAddress}, ${userDetails.affiliateDetailForInvoice.secondAddress}, ${userDetails.affiliateDetailForInvoice.city}, ${userDetails.affiliateDetailForInvoice.state}, ${userDetails.affiliateDetailForInvoice.pinCode}`}
                                    </p>
                                    <p className="text-sm font-medium text-gray-700">
                                        Place of Supply:{" "}
                                        <span className="text-gray-600 font-normal">
                                            {`${userDetails.affiliateDetailForInvoice.state}, ${userDetails.affiliateDetailForInvoice.country}`}
                                        </span>
                                    </p>
                                </>
                                :
                                <></>
                        }
                    </div>
                </div>
            </div>

            {/* JOURNEY DETALS */}
            <div className="mb-6">
                {/* <div className="flex items-center gap-2 mb-3">
                    <div className="h-5 w-1 bg-blue-500 rounded-full"></div>
                    <p className="text-base font-semibold text-gray-800">
                        Transaction Details: Air Ticket
                    </p>
                </div> */}

                {/* Table - Simplified without fare breakdown */}
                <div className="overflow-x-auto rounded-md border border-gray-300 html2pdf__page-break--avoid">
                    <Table className="min-w-full">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                                    Ticket/PNR
                                </th>
                                {/* <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                                    Passenger
                                </th> */}
                                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                                    Route
                                </th>
                                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                                    Flight Details
                                </th>
                                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                                    Travel Date
                                </th>
                                {/* <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300 text-right">
                  Total
                </th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {/* GALILEO */}
                            {
                                flightTicket?.bookingResponses?.map((journey) =>
                                    journey.galileoBookingDetailResponses?.map((each, index) => (
                                        <tr key={each + "__galileo_booking_detail_response_details" + index}>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                <div className="font-medium">{journey.pnrDetail?.ticketNumber}</div>
                                                <div className="text-gray-600 mt-1">PNR: {journey.pnrDetail?.supplierPnr || "-"}</div>
                                            </td>
                                            {/* <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                {journey.galileoPassengerDetailResponses?.map(each => `${each.title} ${each.firstName} ${each.lastName}`).join(", ")}
                                            </td> */}
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300 font-medium">
                                                <div className="flex items-center gap-0.5">
                                                    {each.origin} <ArrowRight className="size-3.5 text-gray-500" /> {each.destination}
                                                </div>
                                            </td>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                <div className="">
                                                    <div className="font-medium">{each.flightBookingSegments?.map(each => `${each.carrier} ${each.flightNumber}`).join(", ")}</div>
                                                    <div>
                                                        {/* <span className="">MS 969</span> */}
                                                        <span className="text-gray-600 ml-1">({each.flightBookingSegments?.map(each => humanizeText(each.cabin)).join(", ")})</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                {each.flightBookingSegments?.map(each => moment(each.departureTime).format("DD/MM/YYYY HH:mm A")).join(", ")}
                                            </td>
                                            {/* <td className="p-2 text-sm font-medium text-gray-900 border-b border-gray-300 text-right">
                        ₹11,910.00
                      </td> */}
                                        </tr>
                                    )))
                            }
                            {/* NAVITAIRE */}
                            {
                                flightTicket?.bookingResponses?.map((journey) =>
                                    journey.navitaireBookingDetailResponses?.map((each, index) => (
                                        <tr key={each + "__galileo_booking_detail_response_details" + index}>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                <div className="font-medium">{journey.pnrDetail?.ticketNumber}</div>
                                                <div className="text-gray-600 mt-1">PNR: {journey.pnrDetail?.supplierPnr || "-"}</div>
                                            </td>
                                            {/* <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                {journey.galileoPassengerDetailResponses?.map(each => `${each.title} ${each.firstName} ${each.lastName}`).join(", ")}
                                            </td> */}
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300 font-medium">
                                                <div className="flex items-center gap-0.5">
                                                    {each.origin} <ArrowRight className="size-3.5 text-gray-500" /> {each.destination}
                                                </div>
                                            </td>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                <div className="">
                                                    <div className="font-medium">{each.navitaireBookingDetailSegments?.map(each => `${each.carrierCode} ${each.flightNumber}`).join(", ")}</div>
                                                    {/* <div>
                            <span className="">MS 969</span>
                            <span className="text-gray-600 ml-1">({each.navitaireBookingDetailSegments?.map(each => humanizeText(each.cabin)).join(", ")})</span>
                          </div> */}
                                                </div>
                                            </td>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                {each.navitaireBookingDetailSegments?.map(each => moment(each.originDateTime).format("DD/MM/YYYY HH:mm A")).join(", ")}
                                            </td>
                                            {/* <td className="p-2 text-sm font-medium text-gray-900 border-b border-gray-300 text-right">
                        ₹11,910.00
                      </td> */}
                                        </tr>
                                    )))
                            }
                            {/* AKBAR */}
                            {
                                flightTicket?.bookingResponses?.map((journey) =>
                                    journey.akbarBookingDetailResponses?.map((each, index) => (
                                        <tr key={each + "__galileo_booking_detail_response_details" + index}>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                <div className="font-medium">{journey.pnrDetail?.ticketNumber}</div>
                                                <div className="text-gray-600 mt-1">PNR: {journey.pnrDetail?.supplierPnr || "-"}</div>
                                            </td>
                                            {/* <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                {journey.akbarPassengerDetailResponses?.map(each => `${each.title} ${each.firstName} ${each.lastName}`).join(", ")}
                                            </td> */}
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300 font-medium">
                                                <div className="flex items-center gap-0.5">
                                                    {each.origin} <ArrowRight className="size-3.5 text-gray-500" /> {each.destination}
                                                </div>
                                            </td>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                <div className="">
                                                    <div className="font-medium">{each.akbarBookingDetailJourneys?.map(each => `${each.oac} ${each.flightNo}`).join(", ")}</div>
                                                    {/* <div>
                            <span className="">MS 969</span>
                            <span className="text-gray-600 ml-1">({each.navitaireBookingDetailSegments?.map(each => humanizeText(each.cabin)).join(", ")})</span>
                          </div> */}
                                                </div>
                                            </td>
                                            <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                {each.akbarBookingDetailJourneys?.map(each => moment(each.departureTime).format("DD/MM/YYYY HH:mm A")).join(", ")}
                                            </td>
                                            {/* <td className="p-2 text-sm font-medium text-gray-900 border-b border-gray-300 text-right">
                        ₹11,910.00
                      </td> */}
                                        </tr>
                                    )))
                            }
                        </tbody>
                    </Table>
                </div>

                {/* <p className="text-sm mt-2 text-gray-600 italic">IAT(Date Change)</p> */}
            </div>

            {/* FARE BREAKUP */}
            <div className="flex justify-between items-end gap-4 mb-8" html2pdf__page-break--avoid>
                <div className="w-6/12 h-fit">
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-800">
                            Amount in words:
                            <div className="font-normal text-gray-600">
                                {numberToWords(fareDetails.fareBreakup.totalFare)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end w-5/12">
                    <RefundFeeSummary
                        service="FLIGHT"
                        totalFare={fareDetails.fareBreakup.totalFare}
                        refundCharges={manageFlightCharges(flightTicket)}
                    />
                </div>
            </div>

            {/* Payment Instructions - Simple Version */}
            <div className="mb-6 text-sm border-t border-gray-300 pt-4 text-gray-800 html2pdf__page-break--avoid">
                <p className="mb-1"><span className="font-medium">Cash:</span> Payment should be made directly to chief cashier in our office and to no one else.</p>
                <p className="mb-1"><span className="font-medium">Receipts:</span> Receipt on our official receipt form duly signed by our cashier only will be considered valid.</p>
                <p className="mb-1"><span className="font-medium">Cheque:</span> All cheques should be drawn in favour of Paul Merchants Ltd and crossed a/c payee.</p>
                <p className="mb-1"><span className="font-medium">Outstandings:</span> We reserve the right to charge interest @ 24% per annum on all outstanding overdue account.</p>
                <p className="mt-2 mb-1"><span className="font-medium">All payments to be transferred directly into below account</span></p>
                <p className="mb-1"><span className="font-medium">HDFC account number 00350330000694 IFSC HDFC0000035</span></p>
                <p className="text-xs">HSN/SAC-996551</p>
            </div>

            {/* FOOTER */}
            <div className="border-t border-gray-300 pt-4 mt-6">
                <div className="flex justify-between items-center">
                    <div className="w-24 h-24">
                        <QRCode
                            size={96}
                            value={`Payment Advice: ${flightTicket?.bookingResponses?.[0]?.invoiceNo || "N/A"
                                }`}
                            className="h-full w-full"
                        />
                    </div>
                    <p className="text-center text-gray-800 text-xs font-medium">
                        This is a computer generated Payment Advice and does not require
                        Signature/Stamp
                    </p>
                    <div className="text-right text-xs text-gray-800">
                        <p>Generated on: {new Date().toLocaleDateString()}</p>
                        <p className="mt-1">Paul Merchants Ltd.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}